import type { DivExtensionClass } from '@divkitframework/divkit/typings/common.d.ts';
import type { Logger } from '@/modules/logger';

type IntercomFn = (...args: unknown[]) => void;

const intercom = (): IntercomFn | undefined => (window as { Intercom?: IntercomFn }).Intercom;

/**
 * Support button in the layout: toggles the Intercom messenger.
 *
 * The funnel registered an empty class here while the layouts kept requesting the extension, so the
 * button rendered and did nothing. Ported from WebQuiz's `IntercomExtensionBuilder`.
 *
 * `onHide` matters: closing the messenger with its own cross must leave this extension thinking it is
 * closed, or the next tap re-hides an already hidden widget and the button looks dead.
 */
export const createIntercomExtension = (logger?: Logger): DivExtensionClass => {
  class IntercomExtension {
    private opened = false;

    private readonly toggle = (): void => {
      try {
        const client = intercom();
        if (!client) return;
        client(this.opened ? 'hide' : 'show');
        this.opened = !this.opened;
      } catch (error) {
        logger?.logWarning({ error: String(error) }, 'intercomToggleFailed');
      }
    };

    mountView(node: HTMLElement): void {
      if (!node) return;
      try {
        // DivKit 32 web gives action-less nodes `pointer-events: none`; the extension is the action here.
        node.style.pointerEvents = 'auto';
        node.style.cursor = 'pointer';
        node.addEventListener('click', this.toggle);
        intercom()?.('onHide', () => {
          this.opened = false;
        });
      } catch (error) {
        logger?.logWarning({ error: String(error) }, 'intercomMountFailed');
      }
    }

    unmountView(node: HTMLElement): void {
      node?.removeEventListener('click', this.toggle);
    }
  }
  return IntercomExtension as unknown as DivExtensionClass;
};
